import React, { useState, useEffect, FC, useCallback } from "react";
import { IconButton, Divider, Box, Typography } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import ApiService from "../services/ApiService";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import NoAuthForm from "./NoAuthForm";
import { Alert } from "@material-ui/lab";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";

export interface NoAuthConfigProps {
  deployedUrl?: string;
  deploymentId: string;
  noAuthResponse?: INoAuthResponse;
  refreshDeployments: () => void;
  teamId?: string;
  deploymentStatus?: string;
  serviceId: string;
}

export interface INoAuthMethod {
  method: string;
  noAuthId: string;
}

export interface INoAuthPathDetail {
  path: string;
  methods: INoAuthMethod[];
}

export interface INoAuthResponse {
  pathVerbDetails: INoAuthPathDetail[];
}

const NoAuthConfig: FC<NoAuthConfigProps> = ({
  deployedUrl,
  deploymentId,
  noAuthResponse,
  refreshDeployments,
  deploymentStatus,
    serviceId
}) => {
  const [noAuthId, setNoAuthId] = useState<string>("");
  const [noAuth, setNoAuth] = useState<INoAuthResponse>();

  const init = useCallback(() => {
    setNoAuth(noAuthResponse);
  }, [noAuthResponse]);

  const [state, { subscribe: deleteNoAuth }] = useAsyncAction(() =>
    ApiService.deleteNoAuthConfiguration(deploymentId, noAuthId)
  );

  useEffect(() => {
    const isLoaded = state?.loaded;
    const hasError = state?.error;
    if (isLoaded && !hasError) {
      init();
    }
  }, [state, init]);

  const onDeleteNoAuth = (noAuthId: string) => {
    setNoAuthId(noAuthId);
  };

  useEffect(() => {
    if (!noAuthId || !refreshDeployments || !deleteNoAuth) {
      return;
    }
    deleteNoAuth();
  }, [noAuthId, deleteNoAuth, refreshDeployments]);

  useEffect(() => {
    if (!refreshDeployments) {
      return;
    }
    if (state.loaded && !state.error) {
      refreshDeployments();
    }
  }, [state.loaded, state.error, refreshDeployments]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <>
      <Box style={{ padding: "20px" }}>
        <Typography variant="body1" style={{ fontWeight: 600 }}>
          No Auth Configuration
        </Typography>
        <NoAuthForm
          deployedUrl={deployedUrl}
          deploymentId={deploymentId}
          refreshDeployments={refreshDeployments}
          deploymentStatus={deploymentStatus}
          serviceId={serviceId}
        />
        {noAuth &&
          noAuth.pathVerbDetails.map((pathVerbDetail) => {
            return (
              <>
                <div className="noauthdata">
                  <div className="endpoint">{pathVerbDetail.path}</div>
                  {pathVerbDetail.methods.map((method) => (
                    <>
                      <div className="verb">{method.method}</div>
                      <div className="cancel">
                        <IconButton
                          size="small"
                          aria-label="close"
                          style={{ color: "#A9A9A9" }}
                          disabled={
                            deploymentStatus === "DEPLOYING" ||
                            deploymentStatus === "DEPLOY_INITIATED"
                              ? true
                              : false
                          }
                        >
                          <CancelIcon
                            fontSize="small"
                            onClick={() => {
                              onDeleteNoAuth(method.noAuthId);
                            }}
                          />
                        </IconButton>
                      </div>
                    </>
                  ))}
                  <div className="divider">
                    <Divider />
                  </div>
                </div>
              </>
            );
          })}
          {state?.error && <Alert severity="error">{resolveAPIErrorMessage(state?.error)}</Alert>}
      </Box>
    </>
  );
};

export default NoAuthConfig;
